import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApplyChargeRuleResponse,
  CreatePurchaseOrderResponse,
  ListPurchaseOrdersResponse,
  PoChargesResponse,
  PurchaseOrderDetail,
  UpdatePoStatusResponse,
  UpdatePurchaseOrderLineResponse,
  type ApplyChargeRuleRequest,
  type ApplyChargeRuleRow,
  type CreateLotReturnRequest,
  type CreatePoChargeRequest,
  type CreatePurchaseOrderRequest,
  type PoChargeKind,
  type PoChargeTreatment,
  type PoLandedSummary,
  type PoStatus,
  type PurchaseOrderLine,
  type PurchaseOrderSummary,
  type UpdatePoChargeRequest,
} from '@sourcito/schemas';
import { apiFetch } from './api';
import { dollarsInputToCents } from './operating-expenses';
import { formatFinanceCurrency, formatFinanceDelta } from './finance-summary';

/** M1 Orders module. Both hooks assume the caller is behind useModuleEnabled('orders'). */

export function usePurchaseOrders(enabled = true) {
  return useQuery({
    queryKey: ['purchase-orders'] as const,
    enabled,
    queryFn: () => apiFetch('/api/orders', { responseSchema: ListPurchaseOrdersResponse }),
  });
}

export function usePurchaseOrder(id: string | null) {
  return useQuery({
    queryKey: ['purchase-order', id] as const,
    enabled: id != null,
    queryFn: () => apiFetch(`/api/orders/${id}`, { responseSchema: PurchaseOrderDetail }),
  });
}

export function useCreatePurchaseOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePurchaseOrderRequest) =>
      apiFetch('/api/orders', {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: CreatePurchaseOrderResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

/** Call-time id (not hook-bound) — mirrors useUpdateClaimStatus (lib/claims.ts):
 *  needed both by the create-PO dialog (id only exists after the create
 *  call resolves) and by a PO detail's own "Advance to <next>" button. */
export function useAdvancePoStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; status: PoStatus }) =>
      apiFetch(`/api/orders/${vars.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: vars.status }),
        responseSchema: UpdatePoStatusResponse,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['purchase-order', vars.id] });
    },
  });
}

/** R2 item 9 (2026-09-03 intake sanity checks) fix pass I7: clears a
 *  held line's review hold, with optional qty/unitCostCents/unitsPerCase
 *  corrections. Owner/operator only — the route 403s anyone else. */
export function useClearReviewForPoLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      poId: string;
      lineId: string;
      qty?: number;
      unitCostCents?: number;
      unitsPerCase?: number | null;
    }) =>
      apiFetch(`/api/orders/${vars.poId}/lines/${vars.lineId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          clearReview: true,
          qty: vars.qty,
          unitCostCents: vars.unitCostCents,
          unitsPerCase: vars.unitsPerCase,
        }),
        responseSchema: UpdatePurchaseOrderLineResponse,
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['purchase-order', vars.poId] });
    },
  });
}

// ─────────────────────────────────────────────────────────
// OrdersPage toolbar: search (vendor / external id / line item#+description)
// + column sort (orderedAt, vendor, total, status). Pure helpers live here so
// they're unit-testable without rendering — see orders.test.ts. Mirrors
// amazon.ts's search+sort pattern (see AmazonPage/lib/amazon.ts).
// ─────────────────────────────────────────────────────────

export type OrderSearchLine = Pick<PurchaseOrderLine, 'itemNo' | 'description'>;

/** Case-insensitive substring match across vendor, external id, and — when
 *  line detail is available — any line's item# or description. `lines` is
 *  undefined until that order's detail has been fetched (list endpoint is
 *  summary-only); an order with unfetched lines still matches on its own
 *  fields, it just can't be found by line content yet. */
export function matchesOrderSearch(
  order: Pick<PurchaseOrderSummary, 'vendor' | 'externalId'>,
  lines: OrderSearchLine[] | undefined,
  query: string
): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  if (order.vendor.toLowerCase().includes(trimmed)) return true;
  if (order.externalId?.toLowerCase().includes(trimmed)) return true;
  return (lines ?? []).some(
    (l) =>
      (l.itemNo?.toLowerCase().includes(trimmed) ?? false) ||
      l.description.toLowerCase().includes(trimmed)
  );
}

export function searchPurchaseOrders<
  T extends Pick<PurchaseOrderSummary, 'id' | 'vendor' | 'externalId'>,
>(orders: T[], linesById: Map<string, OrderSearchLine[]>, query: string): T[] {
  return orders.filter((o) => matchesOrderSearch(o, linesById.get(o.id), query));
}

/**
 * Fetches line detail for the given order ids in parallel, ONLY when
 * `enabled` (callers gate this on a non-empty search query so a normal page
 * load doesn't fire N detail requests). Shares the `['purchase-order', id]`
 * cache key with usePurchaseOrder, so an already-expanded order's lines are
 * reused rather than re-fetched.
 */
export function useOrderSearchLines(
  orderIds: string[],
  enabled: boolean
): Map<string, OrderSearchLine[]> {
  const results = useQueries({
    queries: orderIds.map((id) => ({
      queryKey: ['purchase-order', id] as const,
      queryFn: () => apiFetch(`/api/orders/${id}`, { responseSchema: PurchaseOrderDetail }),
      enabled,
      staleTime: 5 * 60 * 1000,
    })),
  });
  return useMemo(() => {
    const map = new Map<string, OrderSearchLine[]>();
    orderIds.forEach((id, i) => {
      const data = results[i]?.data;
      if (data) {
        map.set(
          id,
          data.lines.map((l) => ({ itemNo: l.itemNo, description: l.description }))
        );
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderIds, results]);
}

export type OrderSortKey = 'orderedAt' | 'vendor' | 'total' | 'status';
export type OrderSortDirection = 'asc' | 'desc';

const ORDER_STATUS_ORDER: Record<PurchaseOrderSummary['status'], number> = {
  placed: 0,
  confirmed: 1,
  in_transit: 2,
  received_partial: 3,
  closed: 4,
};

// Forward-only sequence, same shape as the API's PO_STATUS_ORDER
// (lib/orders/repo.ts) — derived from ORDER_STATUS_ORDER above so the two
// never drift apart.
const PO_STATUS_SEQUENCE = (
  Object.entries(ORDER_STATUS_ORDER) as Array<[PurchaseOrderSummary['status'], number]>
)
  .sort((a, b) => a[1] - b[1])
  .map(([status]) => status);

/** The single forward transition from `status`, or null when it's terminal
 *  (closed). Powers the PO detail's "Advance to <next>" button. */
export function nextPoStatus(
  status: PurchaseOrderSummary['status']
): PurchaseOrderSummary['status'] | null {
  const idx = PO_STATUS_SEQUENCE.indexOf(status);
  return idx >= 0 && idx < PO_STATUS_SEQUENCE.length - 1 ? PO_STATUS_SEQUENCE[idx + 1]! : null;
}

type OrderSortable = Pick<PurchaseOrderSummary, 'orderedAt' | 'vendor' | 'totalCents' | 'status'>;

/** `orderedAt` is nullable — treated as earliest (epoch 0) so undated orders
 *  sort predictably instead of throwing on Date.parse(null). */
export function compareOrders(
  a: OrderSortable,
  b: OrderSortable,
  key: OrderSortKey,
  direction: OrderSortDirection
): number {
  let cmp: number;
  switch (key) {
    case 'orderedAt':
      cmp =
        (a.orderedAt ? Date.parse(a.orderedAt) : 0) - (b.orderedAt ? Date.parse(b.orderedAt) : 0);
      break;
    case 'vendor':
      cmp = a.vendor.localeCompare(b.vendor);
      break;
    case 'total':
      cmp = a.totalCents - b.totalCents;
      break;
    case 'status':
      cmp = ORDER_STATUS_ORDER[a.status] - ORDER_STATUS_ORDER[b.status];
      break;
  }
  return direction === 'asc' ? cmp : -cmp;
}

export function sortPurchaseOrders<T extends OrderSortable>(
  orders: T[],
  key: OrderSortKey,
  direction: OrderSortDirection
): T[] {
  return [...orders].sort((a, b) => compareOrders(a, b, key, direction));
}

// ─────────────────────────────────────────────────────────
// Manual PO create form: pure line-row helpers, unit-tested here so the
// dollar/qty/description validation logic doesn't require rendering
// (OrdersPage.tsx's create dialog is the only caller). Mirrors
// parseSupplierCostCents (ProductDetailPage.tsx) and CreateShipmentForm's
// inline validLines gate (ShipmentsPage.tsx), adapted for a required
// (not optional) unit cost.
// ─────────────────────────────────────────────────────────

/** Row shape as typed in the create-PO form — all fields are strings while
 *  being edited; validated/converted by toValidPoLines below. */
export interface PoLineDraft {
  description: string;
  itemNo: string;
  upc: string;
  qty: string;
  cost: string; // dollars, as typed
}

export interface ValidPoLine {
  description: string;
  itemNo?: string;
  upc?: string;
  qty: number;
  unitCostCents: number;
}

/** Dollar-string -> integer cents. Unlike parseSupplierCostCents (an
 *  optional field), unit cost is REQUIRED here, so an empty string is
 *  'invalid' rather than null. */
export function parsePoLineCostCents(raw: string): number | 'invalid' {
  const trimmed = raw.trim();
  if (trimmed === '') return 'invalid';
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return 'invalid';
  return Math.round(parsed * 100);
}

/** A draft row is valid when description is non-empty, qty is a positive
 *  integer, and cost parses to a non-negative dollar amount. */
export function isValidPoLineDraft(
  row: Pick<PoLineDraft, 'description' | 'qty' | 'cost'>
): boolean {
  const qty = Number(row.qty);
  return (
    row.description.trim().length > 0 &&
    Number.isInteger(qty) &&
    qty > 0 &&
    parsePoLineCostCents(row.cost) !== 'invalid'
  );
}

/** Filters+converts draft rows into API-ready lines, dropping invalid ones
 *  and preserving order — the submit-gate + payload builder for the
 *  create-PO form. */
export function toValidPoLines(rows: PoLineDraft[]): ValidPoLine[] {
  const result: ValidPoLine[] = [];
  for (const row of rows) {
    if (!isValidPoLineDraft(row)) continue;
    const cost = parsePoLineCostCents(row.cost);
    if (cost === 'invalid') continue; // unreachable given the guard above
    result.push({
      description: row.description.trim(),
      qty: Number(row.qty),
      unitCostCents: cost,
      itemNo: row.itemNo.trim() || undefined,
      upc: row.upc.trim() || undefined,
    });
  }
  return result;
}

/** Sum of qty * unitCostCents — the running total shown while building a
 *  manual PO. */
export function orderTotalCents(lines: Array<Pick<ValidPoLine, 'qty' | 'unitCostCents'>>): number {
  return lines.reduce((sum, l) => sum + l.qty * l.unitCostCents, 0);
}

// ─────────────────────────────────────────────────────────
// Vendor charges & credits (R1 item 5, 2026-09-01 design doc). Charges
// live at a dedicated query key (['purchase-order', poId, 'charges'])
// distinct from the PO detail key, so a mutation invalidates both
// explicitly. Every write also invalidates the orders list, finance
// summary, and the dashboard (landed COGS/cash-out change on all three).
// ─────────────────────────────────────────────────────────

export function poChargesQueryKey(poId: string) {
  return ['purchase-order', poId, 'charges'] as const;
}

export function usePoCharges(poId: string) {
  return useQuery({
    queryKey: poChargesQueryKey(poId),
    queryFn: () => apiFetch(`/api/orders/${poId}/charges`, { responseSchema: PoChargesResponse }),
  });
}

function invalidatePoChargeQueries(qc: ReturnType<typeof useQueryClient>, poId: string) {
  qc.invalidateQueries({ queryKey: ['purchase-orders'] });
  // Fuzzy prefix match: also covers ['purchase-order', poId, 'charges'].
  qc.invalidateQueries({ queryKey: ['purchase-order', poId] });
  qc.invalidateQueries({ queryKey: ['finance-summary'] });
  qc.invalidateQueries({ queryKey: ['dashboard'] });
}

/** Response bodies for create/update/delete aren't part of the committed
 *  zod contract (packages/schemas/src/api.ts only types the GET list +
 *  apply-rule responses) — the UI relies on query invalidation to refresh
 *  state rather than the mutation's own response, so these go unvalidated. */
export function useCreatePoCharge(poId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePoChargeRequest) =>
      apiFetch<unknown>(`/api/orders/${poId}/charges`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidatePoChargeQueries(qc, poId),
  });
}

export function useUpdatePoCharge(poId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { chargeId: string; body: UpdatePoChargeRequest }) =>
      apiFetch<unknown>(`/api/orders/${poId}/charges/${vars.chargeId}`, {
        method: 'PATCH',
        body: JSON.stringify(vars.body),
      }),
    onSuccess: () => invalidatePoChargeQueries(qc, poId),
  });
}

export function useDeletePoCharge(poId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (chargeId: string) =>
      apiFetch<null>(`/api/orders/${poId}/charges/${chargeId}`, { method: 'DELETE' }),
    onSuccess: () => invalidatePoChargeQueries(qc, poId),
  });
}

// ─────────────────────────────────────────────────────────
// R2 item 5 (returned to vendor, 2026-09-03 design doc) — lot returns are
// embedded in usePurchaseOrder's PurchaseOrderDetail (returns[]/lots[]), so
// there's no dedicated GET hook here; only the two mutations, invalidated
// the same way charge mutations are (returns[]/lots[]/unitsReturned all
// live on the same purchase-order query key).
// ─────────────────────────────────────────────────────────

export function useCreateLotReturn(poId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLotReturnRequest) =>
      apiFetch<unknown>(`/api/orders/${poId}/returns`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidatePoChargeQueries(qc, poId),
  });
}

export function useDeleteLotReturn(poId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (returnId: string) =>
      apiFetch<null>(`/api/orders/${poId}/returns/${returnId}`, { method: 'DELETE' }),
    onSuccess: () => invalidatePoChargeQueries(qc, poId),
  });
}

/** D6: card-surcharge rule, preview + apply. Only invalidates on a real
 *  apply (`data.applied`) — a preview call (`apply: false`) changes
 *  nothing server-side, so refetching would be wasted work. */
export function useApplyChargeRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApplyChargeRuleRequest) =>
      apiFetch('/api/orders/charges/apply-rule', {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: ApplyChargeRuleResponse,
      }),
    onSuccess: (data) => {
      if (!data.applied) return;
      qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['purchase-order'] });
      qc.invalidateQueries({ queryKey: ['finance-summary'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

const CHARGE_KIND_LABELS: Record<PoChargeKind, string> = {
  fuel_surcharge: 'Fuel surcharge',
  card_surcharge: 'Card surcharge',
  freight: 'Freight',
  other_fee: 'Other fee',
  credit_memo: 'Credit memo',
};

export function chargeKindLabel(kind: PoChargeKind): string {
  return CHARGE_KIND_LABELS[kind];
}

/** D1: treatment is always the human's choice (the select stays required
 *  and visible), but every kind needs a sensible starting point — every
 *  charge kind defaults to landed_cost; credit_memo defaults to cash_only
 *  since a credit is usually a billing correction on goods already landed,
 *  not a change to their cost. */
export function defaultTreatmentForKind(kind: PoChargeKind): PoChargeTreatment {
  return kind === 'credit_memo' ? 'cash_only' : 'landed_cost';
}

/** Dollar-input string -> signed integer cents, sign enforced by kind
 *  (credit memos negative, everything else positive) — mirrors
 *  dollarsInputToCents (operating-expenses.ts), which only accepts a
 *  positive amount as typed; the minus sign is never typed by the user. */
export function signedAmountForKind(kind: PoChargeKind, dollarsInput: string): number | null {
  const cents = dollarsInputToCents(dollarsInput);
  if (cents == null) return null;
  return kind === 'credit_memo' ? -cents : cents;
}

/** The expanded order card's footer line once `landedSummary` is present
 *  (PurchaseOrderDetail.landedSummary — optional for deploy skew). Allowance
 *  is a separate, already-existing field on the PO detail (not part of the
 *  landed-cost ledger), so it's passed alongside rather than folded into
 *  PoLandedSummary itself. */
export function landedSummaryLine(
  summary: PoLandedSummary & { allowanceCents?: number | null },
  currency: string
): string {
  const parts = [`Subtotal ${formatFinanceCurrency(summary.subtotalCents, currency)}`];
  if (summary.allowanceCents != null && summary.allowanceCents > 0) {
    parts.push(`Allowances ${formatFinanceDelta(-summary.allowanceCents, currency)}`);
  }
  if (summary.chargesCents !== 0) {
    parts.push(`Charges +${formatFinanceCurrency(Math.abs(summary.chargesCents), currency)}`);
  }
  if (summary.creditsCents !== 0) {
    parts.push(`Credits ${formatFinanceDelta(-Math.abs(summary.creditsCents), currency)}`);
  }
  parts.push(`Landed ${formatFinanceCurrency(summary.landedCents, currency)}`);
  const pct = summary.landedUpliftBps / 100;
  parts.push(`${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% per unit`);
  // R2 item 5 (returned to vendor): additive display only.
  if (summary.unitsReturned > 0) {
    parts.push(`Returned ${summary.unitsReturned} unit${summary.unitsReturned === 1 ? '' : 's'}`);
  }
  return parts.join(' · ');
}

/** Sum of amountCents for rows the rule would (or did) create — the
 *  preview/apply dialog's running total. Rows that already existed or were
 *  skipped (no base to charge against) don't count. */
export function chargeRuleTotal(rows: readonly ApplyChargeRuleRow[]): number {
  return rows.filter((r) => r.status === 'created').reduce((sum, r) => sum + r.amountCents, 0);
}

/** Prefill for the "Apply card surcharge" dialog's vendor field — the
 *  vendor with the most purchase orders in the already-loaded list. */
export function mostCommonVendor(orders: readonly Pick<PurchaseOrderSummary, 'vendor'>[]): string {
  const counts = new Map<string, number>();
  for (const o of orders) counts.set(o.vendor, (counts.get(o.vendor) ?? 0) + 1);
  let best = '';
  let bestCount = 0;
  for (const [vendor, count] of counts) {
    if (count > bestCount) {
      best = vendor;
      bestCount = count;
    }
  }
  return best;
}

export interface VendorInvoiceOption {
  id: string;
  label: string;
}

/** "Applied to invoice" select for a credit memo — this vendor's other
 *  invoice-type orders (docType 'invoice'), excluding the PO being edited.
 *  Sourced from the already-loaded purchase-orders list, not a new fetch. */
export function vendorInvoiceOptions(
  orders: readonly PurchaseOrderSummary[],
  vendor: string,
  excludePoId: string
): VendorInvoiceOption[] {
  return orders
    .filter((o) => o.vendor === vendor && o.docType === 'invoice' && o.id !== excludePoId)
    .map((o) => ({
      id: o.id,
      label: `${o.externalId ?? 'no invoice #'}${o.orderedAt ? ` — ${new Date(o.orderedAt).toLocaleDateString()}` : ''}`,
    }));
}
